/**
 * 油漆桶（补齐 registry.ts 早先登记的占位工具 / DESIGN.md §5.1、§2.11 填充口径）。
 *
 * Photoshop 的油漆桶 = 点一下先按容差/连续性泛洪出一块蒙版，再用这块蒙版
 * （而不是当前选区）当 applyFill 的调制层填纯色——跟"选区内填充前景色"
 * （selection-edit.ts 的 fillSelectionWithColor）走的是同一条落盘通道，
 * 区别只是蒙版从哪来。选区仍然会裁一遍：`store.selection` 存在时，先把
 * 泛洪蒙版按选区相交，保证"选区外点不到"这条 Photoshop 惯例成立。
 */
import { compositeDocument } from './composite';
import { applyFill } from '../engine/fill/apply';
import { magicWand, type MagicWandOptions } from '../engine/selection/magic-wand';
import type { AppStore, RgbColor } from './store';

/**
 * 在文档坐标 (x, y) 处按容差泛洪，把泛洪出的区域填成 `color`。
 * 无文档/无活动 raster 图层/点在画布外/泛洪结果为空都是无操作。
 * 一次调用 = 一条历史。返回是否有修改发生。
 */
export async function paintBucketFill(
  store: AppStore,
  x: number,
  y: number,
  color: RgbColor,
  options: MagicWandOptions,
): Promise<boolean> {
  const document = store.document;
  if (document === null) return false;
  const seedX = Math.floor(x);
  const seedY = Math.floor(y);
  if (seedX < 0 || seedY < 0 || seedX >= document.size.width || seedY >= document.size.height) {
    return false;
  }
  const target = store.activeRasterLayerId();
  if (target === null) return false;

  const composite = compositeDocument(document, store.tiles);
  const flooded = await magicWand(composite, seedX, seedY, options);
  if (flooded.isEmpty) return false;

  const selection = store.selection;
  const mask = selection === null ? flooded : { valueAt: (px: number, py: number) => (flooded.valueAt(px, py) * selection.valueAt(px, py)) / 255 };
  const bounds = flooded.contentBounds();
  if (bounds === null) return false;

  const transaction = store.beginPixelTransaction('油漆桶');
  const touched = applyFill(
    store.tiles,
    target,
    bounds,
    () => [color.r, color.g, color.b, 255],
    transaction,
    { selection: mask },
  );
  if (touched.length === 0) return false;
  store.commitPixels('油漆桶', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
